import clsx from 'clsx';
import {
  type ComponentProps,
  type PropsWithChildren,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';

export default function FilterList(
  props: PropsWithChildren<{ className?: string | undefined }>,
) {
  return (
    <>
      <div className={clsx('hidden w-full flex-wrap lg:flex', props.className)}>
        {props.children}
      </div>
      <Scroller className="hidden max-lg:block">{props.children}</Scroller>
    </>
  );
}

function Scroller(
  props: PropsWithChildren<{ className?: ComponentProps<'div'>['className'] }>,
) {
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fadeStartRef = useRef<HTMLDivElement>(null);
  const fadeEndRef = useRef<HTMLDivElement>(null);
  const [[fadeStart, fadeEnd], setFades] = useState<[boolean, boolean]>([
    false,
    false,
  ]);

  useEffect(() => {
    const rootEl = rootRef.current;
    const scrollEl = scrollRef.current;

    if (!(rootEl && scrollEl)) return;

    const handleScrollWheel = (event: WheelEvent) => {
      event.preventDefault();

      let left = 0;

      // https://developer.mozilla.org/en-US/docs/Web/API/WheelEvent/deltaMode
      // delta is provided in pixels
      if (event.deltaMode === 0) {
        // this event fires for trackpad gestures as well - there's no reliable
        // way to distinguish the input type from the event, so we consider deltaX
        // if it's large enough
        left = Math.abs(event.deltaX) > 10 ? event.deltaX : event.deltaY;
      }
      // delta is provided in terms of lines or pages
      // since is high effort to determine what this means in terms of pixels
      // (see https://stackoverflow.com/a/37474225)
      // use a default scroll value
      else {
        left = event.deltaY > 0 ? 20 : -20;
      }

      scrollEl.scrollBy({ left, behavior: 'auto' });
    };

    const updateFade = () => {
      const { scrollWidth, offsetWidth, scrollLeft } = scrollEl;

      setFades([scrollLeft > 0, scrollLeft - scrollWidth + offsetWidth < 0]);
    };

    updateFade();
    scrollEl.addEventListener('scroll', updateFade);
    window.addEventListener('resize', updateFade);
    rootEl.addEventListener('wheel', handleScrollWheel);

    return () => {
      scrollEl.removeEventListener('scroll', updateFade);
      window.removeEventListener('resize', updateFade);
      rootEl.removeEventListener('wheel', handleScrollWheel);
    };
  }, []);

  return (
    <div
      className={clsx(
        'relative size-full overflow-hidden [scrollbar-width:none]',
        props.className,
      )}
      ref={rootRef}
    >
      <Fade position="start" show={fadeStart} ref={fadeStartRef} />
      <Fade position="end" show={fadeEnd} ref={fadeEndRef} />
      <div
        className="w-full flex items-center gap-1.5 flex-nowrap z-0 overflow-x-auto [scrollbar-width:none]"
        ref={scrollRef}
      >
        {props.children}
      </div>
    </div>
  );
}

const Fade = forwardRef<
  HTMLDivElement,
  { position: 'start' | 'end'; show: boolean }
>((props, ref) => {
  return (
    <div
      className={clsx(
        'z-10 absolute h-full w-[24px] flex opacity-0 transition-opacity pointer-events-none from-white/100 to-white/0',
        props.position === 'start'
          ? 'start-0 bg-gradient-to-r'
          : 'end-0 flex-row-reverse bg-gradient-to-l',
        props.show ? 'opacity-100' : 'opacity-0 pointer-events-none',
      )}
      ref={ref}
    />
  );
});
